/**
 * @file ページの対応から、いまのページに関係するページを引く (純関数)。
 *
 * @what: 自分の対応 (このページ → 相手のページ) と、自分を相手に持つ資料の対応
 *   (その資料のページ → このページ) を、資料ごとにまとめて返す。見出しは両側の目次から引く。
 * @why: 配色レシピの配色のページ ↔ カラーイメージチャートの語のページのように、ページどうしを
 *   両向きに行き来する。対応は片側の資料だけが持ち、逆向きは読むときに引く。見出しを対応に
 *   持たないのは、目次と同じ文字列を二重に持つとずれるためである。
 * @failure: 例外を投げない。本だなに無い資料を指す対は出さない。
 */

import type { StudySourcePageLink } from "../api/sourcePageLinks";
import type { StudySource } from "../api/sources";
import { tocEntriesAt } from "./sourcePages";

/** 関係するページの1件で、相手の資料のページとその目次の見出しを持つ。 */
export interface RelatedPage {
  page: number;
  /** 目次の見出しで、見出しが無いページでは空文字になる。 */
  label: string;
}

/** 関係するページ (資料ごと、ページ順)。 */
export interface RelatedPageGroup {
  source: StudySource;
  pages: RelatedPage[];
}

/** ページの対応で sourceId を相手に持つ資料を返す。 */
export function pageLinkReferrersOf(
  sources: readonly StudySource[],
  sourceId: string,
): StudySource[] {
  return sources.filter((s) => s.page_link_targets?.includes(sourceId));
}

/** 目次でそのページが属する見出し (同じページから始まる見出しは読点でつなぐ)。 */
function headingAt(source: StudySource, page: number): string {
  return tocEntriesAt(source.toc, page)
    .map((e) => e.title)
    .join("、");
}

/**
 * いまのページに関係するページ。
 *
 * @what: 自分の対応が先、自分を指す資料が後の順で資料ごとにまとめ、同じ資料のページは
 *   重複を除いてページ順に並べる。
 */
export function relatedPagesAt(
  source: StudySource,
  ownLinks: readonly StudySourcePageLink[],
  referrers: readonly { source: StudySource; links: readonly StudySourcePageLink[] }[],
  sources: readonly StudySource[],
  page: number,
): RelatedPageGroup[] {
  const groups = new Map<string, { source: StudySource; pages: Set<number> }>();
  const add = (target: StudySource, targetPage: number) => {
    const g = groups.get(target.id) ?? { source: target, pages: new Set<number>() };
    g.pages.add(targetPage);
    groups.set(target.id, g);
  };
  for (const l of ownLinks) {
    if (l.page !== page || l.target === source.id) continue;
    const target = sources.find((s) => s.id === l.target);
    if (target) add(target, l.target_page);
  }
  for (const ref of referrers) {
    if (ref.source.id === source.id) continue;
    for (const l of ref.links) {
      if (l.target === source.id && l.target_page === page) add(ref.source, l.page);
    }
  }
  return [...groups.values()].map((g) => ({
    source: g.source,
    pages: [...g.pages]
      .sort((a, b) => a - b)
      .map((p) => ({ page: p, label: headingAt(g.source, p) })),
  }));
}
